import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent, NavigationService } from '@memberjunction/ng-shared';
import { RunQuery, CompositeKey, Metadata } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';

// ── Types ──

interface OrgListItem {
  organizationID: string;
  organizationName: string;
  conversationCount: number;
  activeUsers: number;
  totalRuns: number;
  successRate: number;
  totalCost: number;
  lastActivityAt: string | null;
}

interface PersonActivity {
  personID: string;
  personName: string;
  conversationCount: number;
}

interface ConversationListItem {
  conversationID: string;
  name: string | null;
  personID: string;
  personName: string;
  exchangeCount: number;
  hasFailures: boolean;
  createdAt: string;
}

interface ComponentListItem {
  componentID: string;
  componentName: string;
  type: string | null;
  title: string | null;
  versionCount: number;
  avgRating: number | null;
  totalFeedbackCount: number;
  totalViews: number;
  lastGenerated: string | null;
}

interface DailyActivityRow {
  day: string;
  conversationCount: number;
}

interface DailyCostRow {
  day: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface PersonCostRow {
  personID: string;
  personName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface ModelCostRow {
  modelName: string;
  vendorName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface Exchange {
  id: string;
  role: string;
  message: string | null;
  personID: string | null;
  aiAgentRunID: string | null;
  userComponentVersionID: string | null;
  createdAt: string;
  agentRunStatus: string | null;
  agentRunError: string | null;
  agentRunDurationMs: number | null;
}

type DetailView = 'list' | 'org-detail' | 'conversation';
type OrgSubTab = 'activity' | 'components' | 'costs';
type TimePreset = 'this-month' | 'last-month' | '7d' | 'all-time' | 'custom';
type SortColumn = 'organizationName' | 'conversationCount' | 'activeUsers' | 'totalRuns' | 'successRate' | 'totalCost' | 'lastActivityAt';
type SortDirection = 'asc' | 'desc';

@Component({
  standalone: false,
  selector: 'skip-monitoring-orgs',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.css']
})
@RegisterClass(BaseResourceComponent, 'SkipMonitoringOrgsResource')
export class SkipMonitoringOrgsComponent extends BCBaseComponent implements OnInit, OnDestroy {
  // Authorization
  IsAuthorizedUser = false;
  IsInitializing = true;

  // Navigation state
  DetailView: DetailView = 'list';
  OrgSubTab: OrgSubTab = 'activity';
  SelectedOrg: OrgListItem | null = null;
  SelectedConversation: ConversationListItem | null = null;
  SelectedPersonID = '';

  // Time range
  TimePreset: TimePreset = 'this-month';
  CustomStartDate = '';
  CustomEndDate = '';

  // Level 1: Org list
  Organizations: OrgListItem[] = [];
  OrgListLoading = false;
  OrgSearchTerm = '';
  SortColumn: SortColumn = 'conversationCount';
  SortDirection: SortDirection = 'desc';

  // Level 2 - Activity sub-tab
  PersonActivities: PersonActivity[] = [];
  Conversations: ConversationListItem[] = [];
  DailyActivity: DailyActivityRow[] = [];
  ActivityLoading = false;
  ConversationsLoading = false;
  MaxDailyActivity = 0;
  NiceMaxActivity = 0;

  // Level 2 - Components sub-tab
  Components: ComponentListItem[] = [];
  FilteredComponents: ComponentListItem[] = [];
  ComponentsLoading = false;
  ComponentSearch = '';
  ComponentSortColumn: keyof ComponentListItem = 'lastGenerated';
  ComponentSortDirection: 'asc' | 'desc' = 'desc';
  ComponentTypeFilter = '';

  // Level 2 - Costs sub-tab
  DailyCosts: DailyCostRow[] = [];
  ContactCosts: PersonCostRow[] = [];
  ModelCosts: ModelCostRow[] = [];
  CostLoading = false;
  MonthlyTotalCost = 0;
  MonthlyTotalTokens = 0;
  MonthlyTotalRuns = 0;

  // Level 3: Conversation detail
  Exchanges: Exchange[] = [];
  ExchangesLoading = false;

  // Error
  Error: string | null = null;

  private cdr = inject(ChangeDetectorRef);
  protected override navigationService = inject(NavigationService);

  constructor() {
    super();
  }

  async GetResourceDisplayName(): Promise<string> {
    return 'Skip Monitoring - Organizations';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-building';
  }

  // ========== Lifecycle ==========

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.checkAuthorization();
    if (this.IsAuthorizedUser) {
      await this.LoadOrganizations();
    }
    this.NotifyLoadComplete();
  }

  override ngOnDestroy(): void {
    // cleanup if needed
  }

  private async checkAuthorization(): Promise<void> {
    const ctx = this.tenantContextService.currentContext;
    const memberships = this.tenantContextService.GetAllMemberships();
    const isSystemUser = !ctx?.personID && memberships.length === 0;
    const currentUser = Metadata.Provider.CurrentUser;
    const hasIntegrationRole = currentUser?.UserRoles?.some(
      r => r.Role?.toLowerCase() === 'integration'
    ) ?? false;

    this.IsAuthorizedUser = isSystemUser || hasIntegrationRole;
    this.IsInitializing = false;
    this.cdr.detectChanges();
  }

  // ========== Time Range ==========

  private getTimeRange(): { start: string; end: string } {
    const now = new Date();
    if (this.TimePreset === 'this-month') {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: start.toISOString(), end: now.toISOString() };
    }
    if (this.TimePreset === 'last-month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: start.toISOString(), end: end.toISOString() };
    }
    if (this.TimePreset === '7d') {
      return { start: new Date(now.getTime() - 604800000).toISOString(), end: now.toISOString() };
    }
    if (this.TimePreset === 'all-time') {
      return { start: new Date(2020, 0, 1).toISOString(), end: now.toISOString() };
    }
    if (this.TimePreset === 'custom') {
      if (this.CustomStartDate && this.CustomEndDate) {
        return {
          start: new Date(this.CustomStartDate).toISOString(),
          end: new Date(this.CustomEndDate).toISOString()
        };
      }
    }
    // Fallback: this month
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: now.toISOString() };
  }

  private getMonthRange(): { start: string; end: string } {
    const { start, end } = this.getTimeRange();
    if (this.TimePreset === 'this-month' || this.TimePreset === 'last-month') {
      return { start, end };
    }
    // For 7d/30d presets, use the full month containing the start date
    const startDate = new Date(start);
    const monthStart = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const now = new Date();
    return { start: monthStart.toISOString(), end: now.toISOString() };
  }

  SetTimePreset(preset: TimePreset): void {
    this.TimePreset = preset;
    if (this.DetailView === 'list') {
      this.LoadOrganizations();
    } else if (this.DetailView === 'org-detail') {
      this.loadOrgDetailData();
    }
  }

  // ========== Level 1: Organization List ==========

  async LoadOrganizations(): Promise<void> {
    this.OrgListLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getTimeRange();
      const result = await rq.RunQuery({
        QueryName: 'MonitoringOrgActivity',
        CategoryPath: '/Skip/Monitoring',
        Parameters: { StartDate: start, EndDate: end }
      });

      if (result.Success && result.Results?.length) {
        this.Organizations = result.Results.map((r: Record<string, unknown>) =>
          this.mapOrgListItem(r)
        );
      } else {
        this.Organizations = [];
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load organizations';
    } finally {
      this.OrgListLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapOrgListItem(r: Record<string, unknown>): OrgListItem {
    return {
      organizationID: r['OrganizationID'] as string,
      organizationName: (r['OrganizationName'] as string) || 'Unknown',
      conversationCount: (r['ConversationCount'] as number) || 0,
      activeUsers: (r['ActiveUsers'] as number) || 0,
      totalRuns: (r['TotalRuns'] as number) || 0,
      successRate: (r['SuccessRate'] as number) || 0,
      totalCost: (r['TotalCost'] as number) || 0,
      lastActivityAt: (r['LastActivityAt'] as string) || null
    };
  }

  get FilteredOrganizations(): OrgListItem[] {
    let filtered = this.Organizations;
    if (this.OrgSearchTerm.trim()) {
      const term = this.OrgSearchTerm.toLowerCase();
      filtered = filtered.filter(o => o.organizationName.toLowerCase().includes(term));
    }
    return this.sortOrganizations(filtered);
  }

  private sortOrganizations(orgs: OrgListItem[]): OrgListItem[] {
    const dir = this.SortDirection === 'asc' ? 1 : -1;
    return [...orgs].sort((a, b) => {
      const aVal = a[this.SortColumn];
      const bVal = b[this.SortColumn];
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'string') return (aVal as string).localeCompare(bVal as string) * dir;
      return ((aVal as number) - (bVal as number)) * dir;
    });
  }

  ToggleSort(column: SortColumn): void {
    if (this.SortColumn === column) {
      this.SortDirection = this.SortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.SortColumn = column;
      this.SortDirection = 'desc';
    }
  }

  GetSortIcon(column: SortColumn): string {
    if (this.SortColumn !== column) return 'fa-solid fa-sort';
    return this.SortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down';
  }

  SelectOrg(org: OrgListItem): void {
    this.SelectedOrg = org;
    this.DetailView = 'org-detail';
    this.OrgSubTab = 'activity';
    this.SelectedPersonID = '';
    this.cdr.detectChanges();
    this.loadOrgDetailData();
  }

  // ========== Level 2: Organization Detail ==========

  private loadOrgDetailData(): void {
    switch (this.OrgSubTab) {
      case 'activity':
        this.loadActivityTab();
        break;
      case 'components':
        this.loadComponentsTab();
        break;
      case 'costs':
        this.loadCostsTab();
        break;
    }
  }

  SwitchSubTab(tab: OrgSubTab): void {
    this.OrgSubTab = tab;
    this.loadOrgDetailData();
  }

  BackToOrganizations(): void {
    this.DetailView = 'list';
    this.SelectedOrg = null;
    this.SelectedConversation = null;
    this.SelectedPersonID = '';
    this.resetOrgDetailState();
  }

  private resetOrgDetailState(): void {
    this.PersonActivities = [];
    this.Conversations = [];
    this.DailyActivity = [];
    this.Components = [];
    this.DailyCosts = [];
    this.ContactCosts = [];
    this.ModelCosts = [];
    this.Exchanges = [];
  }

  // ── Activity sub-tab ──

  private async loadActivityTab(): Promise<void> {
    if (!this.SelectedOrg) return;
    await Promise.all([
      this.loadPersonActivities(),
      this.loadConversations(),
      this.loadDailyActivity()
    ]);
  }

  private async loadPersonActivities(): Promise<void> {
    if (!this.SelectedOrg) return;
    this.ActivityLoading = true;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getMonthRange();
      const result = await rq.RunQuery({
        QueryName: 'OrgAnalyticsUsageMetrics',
        CategoryPath: '/Skip/OrgAnalytics',
        Parameters: {
          OrganizationID: this.SelectedOrg.organizationID,
          MonthStart: start,
          MonthEnd: end
        }
      });

      this.PersonActivities = [];
      this.DailyActivity = [];
      this.MaxDailyActivity = 0;

      if (result.Success && result.Results?.length) {
        this.parseUsageResults(result.Results);
      }
      this.NiceMaxActivity = this.computeNiceMax(this.MaxDailyActivity);
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load activity data';
    } finally {
      this.ActivityLoading = false;
      this.cdr.detectChanges();
    }
  }

  private parseUsageResults(results: Record<string, unknown>[]): void {
    for (const row of results) {
      const granularity = row['Granularity'] as string;
      if (granularity === 'Contact') {
        this.PersonActivities.push({
          personID: row['PersonID'] as string,
          personName: (row['PersonName'] as string) || 'Unknown',
          conversationCount: (row['ConversationCount'] as number) || 0
        });
      } else if (granularity === 'Daily') {
        const count = (row['ConversationCount'] as number) || 0;
        this.DailyActivity.push({
          day: row['Day'] as string,
          conversationCount: count
        });
        if (count > this.MaxDailyActivity) this.MaxDailyActivity = count;
      }
    }
  }

  private async loadDailyActivity(): Promise<void> {
    // Daily activity is loaded as part of loadPersonActivities via parseUsageResults
    // This method exists as a named slot for the parallel Promise.all call
  }

  private async loadConversations(): Promise<void> {
    if (!this.SelectedOrg) return;
    this.ConversationsLoading = true;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getTimeRange();
      const params: Record<string, string> = {
        OrganizationID: this.SelectedOrg.organizationID,
        StartDate: start,
        EndDate: end
      };
      if (this.SelectedPersonID) {
        params['PersonID'] = this.SelectedPersonID;
      }

      const result = await rq.RunQuery({
        QueryName: 'MonitoringOrgConversations',
        CategoryPath: '/Skip/Monitoring',
        Parameters: params
      });

      if (result.Success && result.Results?.length) {
        this.Conversations = result.Results.map((r: Record<string, unknown>) =>
          this.mapConversationItem(r)
        );
      } else {
        this.Conversations = [];
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load conversations';
    } finally {
      this.ConversationsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapConversationItem(r: Record<string, unknown>): ConversationListItem {
    return {
      conversationID: r['ConversationID'] as string,
      name: (r['Name'] as string) || null,
      personID: r['PersonID'] as string,
      personName: (r['PersonName'] as string) || 'Unknown',
      exchangeCount: (r['ExchangeCount'] as number) || 0,
      hasFailures: (r['HasFailures'] as number) === 1,
      createdAt: r['CreatedAt'] as string
    };
  }

  FilterByPerson(personID: string): void {
    this.SelectedPersonID = this.SelectedPersonID === personID ? '' : personID;
    this.loadConversations();
  }

  ClearPersonFilter(): void {
    this.SelectedPersonID = '';
    this.loadConversations();
  }

  // ── Components sub-tab ──

  private async loadComponentsTab(): Promise<void> {
    if (!this.SelectedOrg) return;
    this.ComponentsLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getTimeRange();
      const result = await rq.RunQuery({
        QueryName: 'MonitoringOrgComponents',
        CategoryPath: '/Skip/Monitoring',
        Parameters: {
          OrganizationID: this.SelectedOrg.organizationID,
          StartDate: start,
          EndDate: end
        }
      });

      if (result.Success && result.Results?.length) {
        this.Components = result.Results.map((r: Record<string, unknown>) =>
          this.mapComponentItem(r)
        );
      } else {
        this.Components = [];
      }
      this.ApplyComponentFilters();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load components';
    } finally {
      this.ComponentsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapComponentItem(r: Record<string, unknown>): ComponentListItem {
    return {
      componentID: r['ComponentID'] as string,
      componentName: (r['ComponentName'] as string) || 'Unnamed',
      type: (r['Type'] as string) || null,
      title: (r['Title'] as string) || null,
      versionCount: (r['VersionCount'] as number) || 0,
      avgRating: (r['AvgRating'] as number) || null,
      totalFeedbackCount: (r['TotalFeedbackCount'] as number) || 0,
      totalViews: (r['TotalViews'] as number) || 0,
      lastGenerated: (r['LastGenerated'] as string) || null
    };
  }

  // ── Component Search, Sort, Filter ──

  ApplyComponentFilters(): void {
    let filtered = [...this.Components];
    filtered = this.filterComponentsBySearch(filtered);
    filtered = this.filterComponentsByType(filtered);
    filtered = this.sortComponents(filtered);
    this.FilteredComponents = filtered;
  }

  private filterComponentsBySearch(items: ComponentListItem[]): ComponentListItem[] {
    const term = this.ComponentSearch.toLowerCase().trim();
    if (!term) return items;
    return items.filter(c =>
      c.componentName.toLowerCase().includes(term) ||
      (c.title && c.title.toLowerCase().includes(term)) ||
      (c.type && c.type.toLowerCase().includes(term))
    );
  }

  private filterComponentsByType(items: ComponentListItem[]): ComponentListItem[] {
    if (!this.ComponentTypeFilter) return items;
    return items.filter(c => c.type === this.ComponentTypeFilter);
  }

  private sortComponents(items: ComponentListItem[]): ComponentListItem[] {
    const col = this.ComponentSortColumn;
    const dir = this.ComponentSortDirection === 'asc' ? 1 : -1;
    return items.sort((a, b) => {
      const aVal = a[col];
      const bVal = b[col];
      if (aVal === null || aVal === undefined) return dir;
      if (bVal === null || bVal === undefined) return -dir;
      if (typeof aVal === 'string') return aVal.localeCompare(bVal as string) * dir;
      return ((aVal as number) - (bVal as number)) * dir;
    });
  }

  SetComponentSort(column: keyof ComponentListItem): void {
    if (this.ComponentSortColumn === column) {
      this.ComponentSortDirection = this.ComponentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.ComponentSortColumn = column;
      this.ComponentSortDirection = column === 'componentName' ? 'asc' : 'desc';
    }
    this.ApplyComponentFilters();
  }

  GetComponentSortIcon(column: string): string {
    if (this.ComponentSortColumn !== column) return 'fa-sort';
    return this.ComponentSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  get ComponentTypes(): string[] {
    const types = new Set<string>();
    for (const c of this.Components) {
      if (c.type) types.add(c.type);
    }
    return Array.from(types).sort();
  }

  GetStarArray(rating: number | null): boolean[] {
    if (rating === null) return [false, false, false, false, false];
    const rounded = Math.round(rating);
    return [1, 2, 3, 4, 5].map(i => i <= rounded);
  }

  // ── Costs sub-tab ──

  private async loadCostsTab(): Promise<void> {
    if (!this.SelectedOrg) return;
    this.CostLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const { start, end } = this.getMonthRange();
      const result = await rq.RunQuery({
        QueryName: 'OrgAnalyticsCostMetrics',
        CategoryPath: '/Skip/OrgAnalytics',
        Parameters: {
          OrganizationID: this.SelectedOrg.organizationID,
          MonthStart: start,
          MonthEnd: end
        }
      });

      this.resetCostState();
      if (result.Success && result.Results?.length) {
        this.parseCostResults(result.Results);
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load cost data';
    } finally {
      this.CostLoading = false;
      this.cdr.detectChanges();
    }
  }

  private resetCostState(): void {
    this.DailyCosts = [];
    this.ContactCosts = [];
    this.ModelCosts = [];
    this.MonthlyTotalCost = 0;
    this.MonthlyTotalTokens = 0;
    this.MonthlyTotalRuns = 0;
  }

  private parseCostResults(results: Record<string, unknown>[]): void {
    for (const row of results) {
      const granularity = row['Granularity'] as string;
      if (granularity === 'Daily') {
        this.DailyCosts.push({
          day: row['Day'] as string,
          totalCost: (row['TotalCost'] as number) || 0,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
      } else if (granularity === 'Contact') {
        this.ContactCosts.push({
          personID: row['PersonID'] as string,
          personName: (row['PersonName'] as string) || 'Unknown',
          totalCost: (row['TotalCost'] as number) || 0,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
      } else if (granularity === 'Model') {
        this.ModelCosts.push({
          modelName: (row['ModelName'] as string) || 'Unknown',
          vendorName: (row['VendorName'] as string) || 'Unknown',
          totalCost: (row['TotalCost'] as number) || 0,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
      }
    }
    this.ModelCosts.sort((a, b) => b.totalCost - a.totalCost);
    this.MonthlyTotalCost = this.DailyCosts.reduce((sum, d) => sum + d.totalCost, 0);
    this.MonthlyTotalTokens = this.DailyCosts.reduce((sum, d) => sum + d.totalTokens, 0);
    this.MonthlyTotalRuns = this.DailyCosts.reduce((sum, d) => sum + d.runCount, 0);
  }

  get AvgCostPerRun(): number {
    if (!this.MonthlyTotalRuns) return 0;
    return this.MonthlyTotalCost / this.MonthlyTotalRuns;
  }

  get MaxModelCost(): number {
    if (!this.ModelCosts.length) return 0;
    return this.ModelCosts[0].totalCost;
  }

  get MaxContactCost(): number {
    if (!this.ContactCosts.length) return 0;
    return Math.max(...this.ContactCosts.map(c => c.totalCost));
  }

  GetModelBarPercent(cost: number): number {
    if (!this.MaxModelCost || !cost) return 0;
    return (cost / this.MaxModelCost) * 100;
  }

  GetContactCostBarPercent(cost: number): number {
    if (!this.MaxContactCost || !cost) return 0;
    return (cost / this.MaxContactCost) * 100;
  }

  // ========== Level 3: Conversation Detail ==========

  async SelectConversation(conv: ConversationListItem): Promise<void> {
    this.SelectedConversation = conv;
    this.DetailView = 'conversation';
    this.Exchanges = [];
    this.ExchangesLoading = true;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'SkipConversationHistory',
        CategoryPath: '/Skip/Monitoring',
        Parameters: { ConversationID: conv.conversationID }
      });

      if (result.Success && result.Results?.length) {
        this.Exchanges = result.Results.map((r: Record<string, unknown>) =>
          this.mapExchange(r)
        );
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load conversation history';
    } finally {
      this.ExchangesLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapExchange(r: Record<string, unknown>): Exchange {
    return {
      id: r['ID'] as string,
      role: r['Role'] as string,
      message: (r['Message'] as string) || null,
      personID: (r['PersonID'] as string) || null,
      aiAgentRunID: (r['AIAgentRunID'] as string) || null,
      userComponentVersionID: (r['UserComponentVersionID'] as string) || null,
      createdAt: r['CreatedAt'] as string,
      agentRunStatus: (r['AgentRunStatus'] as string) || null,
      agentRunError: (r['AgentRunError'] as string) || null,
      agentRunDurationMs: (r['AgentRunDurationMs'] as number) || null
    };
  }

  BackToOrgDetail(): void {
    this.DetailView = 'org-detail';
    this.SelectedConversation = null;
    this.Exchanges = [];
  }

  OpenAgentRunForm(runID: string): void {
    if (!runID) return;
    this.navigationService.OpenEntityRecord('MJ: AI Agent Runs', CompositeKey.FromID(runID));
  }

  // ========== Chart Helpers ==========

  GetActivityBarHeight(count: number): number {
    if (!this.NiceMaxActivity || !count) return 0;
    return (count / this.NiceMaxActivity) * 100;
  }

  get ActivityYAxisTicks(): number[] {
    if (!this.NiceMaxActivity) return [0];
    const step = this.NiceMaxActivity / 4;
    return [0, step, step * 2, step * 3, this.NiceMaxActivity];
  }

  GetGridlinePosition(tick: number, niceMax: number): number {
    if (!niceMax) return 0;
    return (tick / niceMax) * 100;
  }

  private computeNiceMax(maxValue: number): number {
    if (maxValue <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    if (normalized <= 1) return magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 2.5) return 2.5 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  // ========== Display Helpers ==========

  FormatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  }

  FormatShortDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  FormatCost(cost: number): string {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(2)}`;
  }

  FormatCostPrecise(cost: number): string {
    if (!cost) return '$0.0000';
    return `$${cost.toFixed(4)}`;
  }

  FormatTokens(count: number): string {
    if (!count) return '0';
    if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
    if (count >= 1e3) return `${(count / 1e3).toFixed(1)}K`;
    return count.toString();
  }

  FormatDuration(ms: number | null): string {
    if (ms === null) return '\u2014';
    if (ms < 1000) return `${ms}ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  FormatAxisCount(count: number): string {
    if (count === 0) return '0';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    if (Number.isInteger(count)) return count.toString();
    return count.toFixed(1);
  }

  FormatPercent(value: number): string {
    return `${Math.round(value)}%`;
  }

  GetShortDay(iso: string): string {
    if (!iso) return '';
    return new Date(iso).getDate().toString();
  }

  GetSuccessRateClass(rate: number): string {
    if (rate >= 80) return 'success-rate-good';
    if (rate >= 50) return 'success-rate-warn';
    return 'success-rate-bad';
  }

  GetStatusClass(status: string | null): string {
    if (!status) return '';
    const map: Record<string, string> = {
      Running: 'status-running',
      Completed: 'status-completed',
      Failed: 'status-failed',
      Cancelled: 'status-cancelled'
    };
    return map[status] || '';
  }

  GetDurationClass(ms: number | null): string {
    if (ms === null) return '';
    if (ms > 300000) return 'duration-critical';
    if (ms > 120000) return 'duration-warning';
    return '';
  }

  IsFormMessage(message: string): boolean {
    return message?.startsWith('@{') || message?.startsWith('@{"_mode"');
  }

  ParseFormFields(message: string): { label: string; displayValue: string }[] {
    try {
      const json = JSON.parse(message.substring(1));
      if (json.fields && Array.isArray(json.fields)) {
        return json.fields.map((f: Record<string, string>) => ({
          label: f['label'] || f['name'] || 'Field',
          displayValue: f['displayValue'] || f['value'] || ''
        }));
      }
    } catch {
      // invalid JSON, return empty
    }
    return [];
  }

  GetMessagePreview(message: string | null): string {
    if (!message) return '\u2014';
    if (this.IsFormMessage(message)) return 'Filled out Form';
    return message;
  }

  get TimePresetLabel(): string {
    const labels: Record<string, string> = {
      'this-month': 'This Month',
      'last-month': 'Last Month',
      '7d': 'Last 7 Days',
      'all-time': 'All Time',
      'custom': 'Custom'
    };
    return labels[this.TimePreset] || '';
  }

  ApplyCustomRange(): void {
    this.TimePreset = 'custom';
    if (this.DetailView === 'list') {
      this.LoadOrganizations();
    } else if (this.DetailView === 'org-detail') {
      this.loadOrgDetailData();
    }
  }
}
